import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { NotebookPen, Plus, Trash2, Users, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { dueLabel, exerciseFileUrl } from "@/lib/exercises";
import { FileLinks } from "@/components/exercises/common";
import SubmissionsDialog from "@/components/exercises/SubmissionsDialog";

function ExpandableText({ text, testId }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 220 || text.split("\n").length > 3;
  return (
    <div>
      <p className={`text-sm text-stone-600 whitespace-pre-line break-words ${long && !open ? "line-clamp-3" : ""}`} data-testid={testId}>{text}</p>
      {long && (
        <button type="button" onClick={() => setOpen(!open)} className="mt-1 text-xs font-medium text-[#1C352D] hover:underline inline-flex items-center gap-1" data-testid={`${testId}-toggle`}>
          {open ? <><ChevronUp size={12}/> Mostra meno</> : <><ChevronDown size={12}/> Mostra tutto</>}
        </button>
      )}
    </div>
  );
}

function NewExerciseDialog({ courseId, open, onOpenChange }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ title: "", instructions: "", due_date: "" });
  const [files, setFiles] = useState([]);
  const mut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      files.forEach((x) => fd.append("files", x));
      return (await api.post(`/courses/${courseId}/exercises`, fd)).data;
    },
    onSuccess: () => { toast.success("Esercizio pubblicato"); setF({ title: "", instructions: "", due_date: "" }); setFiles([]); qc.invalidateQueries({ queryKey: ["exercises", courseId] }); onOpenChange(false); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Nuovo esercizio</DialogTitle><DialogDescription>Visibile agli studenti del corso o iscritti al seminario.</DialogDescription></DialogHeader>
        <div><Label className="text-xs">Titolo *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1" data-testid="exercise-title"/></div>
        <div><Label className="text-xs">Istruzioni</Label><Textarea rows={5} value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} className="mt-1" data-testid="exercise-instructions"/></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Scadenza</Label><Input type="datetime-local" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="mt-1" data-testid="exercise-due"/></div>
          <div><Label className="text-xs">Allegati</Label><Input type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} className="mt-1" data-testid="exercise-files"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button className="bg-[#1C352D] hover:bg-[#264A3E]" disabled={!f.title.trim() || mut.isPending} onClick={() => mut.mutate()} data-testid="exercise-save">{mut.isPending ? "Pubblicazione…" : "Pubblica"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CourseExercises({ courseId, canManage }) {
  const qc = useQueryClient();
  const [newOpen, setNewOpen] = useState(false);
  const [openEx, setOpenEx] = useState(null);
  const { data: exs = [] } = useQuery({ queryKey: ["exercises", courseId], queryFn: async () => (await api.get(`/courses/${courseId}/exercises`)).data });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/exercises/${id}`)).data,
    onSuccess: () => { toast.success("Esercizio eliminato"); qc.invalidateQueries({ queryKey: ["exercises", courseId] }); },
  });
  return (
    <Card className="border border-stone-200 rounded-xl shadow-none" data-testid="course-exercises">
      <div className="p-4 border-b border-stone-100 flex items-center justify-between">
        <div className="text-sm font-medium flex items-center gap-2"><NotebookPen size={14}/> Esercizi <span className="text-stone-400 font-normal">· {exs.length}</span></div>
        {canManage && <Button size="sm" className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]" onClick={() => setNewOpen(true)} data-testid="exercise-add-btn"><Plus size={14} className="mr-1"/> Nuovo esercizio</Button>}
      </div>
      {exs.length === 0 ? <div className="p-8 text-center text-sm text-stone-400">Nessun esercizio pubblicato.</div> : (
        <ul className="divide-y divide-stone-100">
          {exs.map((e) => (
            <li key={e.id} className="p-4 flex flex-col sm:flex-row gap-3" data-testid={`exercise-${e.id}`}>
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="font-medium">{e.title}</div>
                <div className="text-[11px] text-stone-500">{dueLabel(e.due_date)} · da {e.created_by_name}</div>
                {e.instructions && <ExpandableText text={e.instructions} testId={`exercise-text-${e.id}`}/>}
                <FileLinks files={e.attachments} urlFor={(fid) => exerciseFileUrl(e.id, fid)} testPrefix="exercise-file"/>
              </div>
              <div className="flex items-start gap-2">
                <div className="text-right text-[11px] text-stone-500 mr-1">
                  <div className="text-lg font-semibold text-[#1C352D]">{e.submitted}</div>consegne
                  {e.counts?.consegnato > 0 && <div className="text-sky-700">{e.counts.consegnato} da valutare</div>}
                </div>
                {e.can_manage && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setOpenEx(e)} data-testid={`exercise-open-${e.id}`}><Users size={13} className="mr-1"/> Consegne</Button>}
                {e.can_manage && <button onClick={() => { if (window.confirm("Eliminare l'esercizio e tutte le consegne?")) del.mutate(e.id); }} className="p-1.5 text-stone-400 hover:text-red-600" data-testid={`exercise-del-${e.id}`}><Trash2 size={14}/></button>}
              </div>
            </li>
          ))}
        </ul>
      )}
      <NewExerciseDialog courseId={courseId} open={newOpen} onOpenChange={setNewOpen}/>
      {openEx && <SubmissionsDialog exercise={openEx} courseId={courseId} onClose={() => setOpenEx(null)}/>}
    </Card>
  );
}
